import Image from "next/image";
import type { MediaAsset } from "@/lib/storefront-types";

type StorefrontIconContext = "navigation" | "feature" | "social";

type StorefrontIconProps = {
  asset?: MediaAsset | null;
  className?: string;
  context: StorefrontIconContext;
  label: string;
};

function normalizeLabel(label: string) {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");
}

function renderNavigationFallback(label: string) {
  const normalized = normalizeLabel(label);

  if (normalized.includes("menu")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M4 12c0-4.1 3.3-7.4 7.4-7.4h1.2C16.7 4.6 20 7.9 20 12"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M6.5 12h11c-.2 3.7-2.4 6-5.5 6s-5.3-2.3-5.5-6Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M9 8.3V6.9M12 7.7V5.8M15 8.3V6.9"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (normalized.includes("more")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <rect
          x="4.5"
          y="5"
          width="15"
          height="14"
          rx="3"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M8 9.5h8M8 12.5h8M8 15.5h5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
      <circle cx="12" cy="12" r="7.5" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M9.5 12h5M12 9.5V15"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function renderFeatureFallback(label: string) {
  const normalized = normalizeLabel(label);

  if (normalized.includes("delivery")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M4 8.5h10v6.5H4zM14 10h3.5l2 2.5V15H14z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <circle cx="8" cy="17" r="1.8" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="17" cy="17" r="1.8" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }

  if (normalized.includes("variety")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M6 11.5h12c0 3.9-2.3 6.2-6 6.2s-6-2.3-6-6.2Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M8.2 11.2c.3-2.4 1.8-4.2 3.8-4.2s3.5 1.8 3.8 4.2"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (normalized.includes("chef")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M8 10.5c-1.4 0-2.5-1-2.5-2.4S6.6 5.7 8 5.7c.8 0 1.5.3 2 .8.6-1.2 1.8-2 3.3-2 2 0 3.7 1.6 3.7 3.6 1.2.2 2.1 1.3 2.1 2.6 0 1.5-1.2 2.8-2.8 2.8H8.7c-1.5 0-2.7-1.2-2.7-2.7"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.2 13.5v4.8h5.6v-4.8"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (normalized.includes("fresh")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M18.5 5.5C12.2 6.2 8.1 10 7 17c5.4-.2 9.6-3.1 11.5-8.7"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.2 17.4c-.7-2.6.2-5.1 2.5-7"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
      <path
        d="m12 4.5 2.1 4.3 4.8.7-3.4 3.3.8 4.7-4.3-2.3-4.3 2.3.8-4.7L5 9.5l4.8-.7L12 4.5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function renderSocialFallback(label: string) {
  const normalized = normalizeLabel(label);

  if (normalized.includes("facebook")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M13.2 20v-6.1h2.3l.3-2.6h-2.6v-1.7c0-.8.2-1.3 1.4-1.3H16V6c-.4-.1-1.3-.2-2.2-.2-2.1 0-3.6 1.3-3.6 3.7v1.8H8v2.6h2.2V20"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (normalized.includes("instagram")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <rect
          x="5"
          y="5"
          width="14"
          height="14"
          rx="4"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="12" r="3.4" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="16.2" cy="7.8" r="0.8" fill="currentColor" />
      </svg>
    );
  }

  if (normalized === "x") {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M6 5h3.3L18 19h-3.3L6 5Zm8.7 0H18l-2.6 3M6 19l5.2-6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (normalized.includes("tiktok")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M14.5 5v8.5a3.5 3.5 0 1 1-2.6-3.4"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M14.5 5c.6 1.8 1.9 3 3.8 3.4"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (normalized.includes("youtube")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <rect
          x="4.5"
          y="7"
          width="15"
          height="10"
          rx="3"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="m10 10 5 2-5 2v-4Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (normalized.includes("whatsapp")) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
        <path
          d="M12 19a6.5 6.5 0 1 0-3.1-.8L6.5 19l.9-2.3A6.4 6.4 0 0 0 12 19Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 9.5c.4 1.7 1.8 3 3.5 3.5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-full w-full">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M12 8v4l2.4 2.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function renderFallback(context: StorefrontIconContext, label: string) {
  switch (context) {
    case "feature":
      return renderFeatureFallback(label);
    case "social":
      return renderSocialFallback(label);
    default:
      return renderNavigationFallback(label);
  }
}

export function StorefrontIcon({
  asset,
  className = "",
  context,
  label,
}: StorefrontIconProps) {
  if (asset?.url) {
    return (
      <span
        className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden ${className}`.trim()}
        aria-hidden="true"
      >
        <Image
          src={asset.url}
          alt={asset.alt?.trim() || label}
          fill
          sizes="32px"
          className="object-contain"
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${className}`.trim()}
      aria-hidden="true"
    >
      {renderFallback(context, label)}
    </span>
  );
}
